import React, { useEffect, useMemo, useRef, useState } from 'react';
import Datepicker, { DateType } from 'react-tailwindcss-datepicker';
import { parseISO } from 'date-fns';
import {
  useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation,
  usePatchStoriesByTripByTripIdStoryAndStoryIdMutation,
} from '../../../store/backendApi';
import { FileUploadResult, useUploadFiles } from '../../../utils/upload';
import { InfoAlert } from '../../../components/alerts/info';
import { SuccessAlert } from '../../../components/alerts/success';
import { ErrorAlert } from '../../../components/alerts/error';
import { imgUrl, thumbnail } from '../../../utils/images';
import DynamicImage from '../../../components/dynamicImage';
import path from 'path-browserify';
import {
  CalendarIcon,
  MapPinIcon,
  PhotoIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Story } from '../../../schema';
import { Dialog } from '@headlessui/react';
import { Button } from '../../../components/Button';

export interface EditStoryModalProps {
  story: Story | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EditStoryModal = (props: EditStoryModalProps) => {
  if (!props.story) {
    return null;
  }
  const tripId = props.story.tripId;

  const [storyDate, setStoryDate] = useState<{ startDate: DateType; endDate: DateType } | null>(
    props.story.date
      ? { startDate: parseISO(props.story.date), endDate: parseISO(props.story.date) }
      : null,
  );
  const [storyPlace, setStoryPlace] = useState<string | undefined>(props.story.place);
  const [storyTitle, setStoryTitle] = useState<string>(props.story.title);
  const [storyDescription, setStoryDescription] = useState<string | undefined>(
    props.story.description,
  );
  const [patchStory, { isLoading: patchStoryLoading }] =
    usePatchStoriesByTripByTripIdStoryAndStoryIdMutation();
  const [callDeleteStory, { isLoading: deleteStoryLoading }] =
    useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [failedEvent, setFailedEvent] = useState(false);
  const [uploadedMedia, setUploadedMedia] = useState<FileUploadResult[]>(
    props.story.medias.map((u) => ({
      success: true,
      fileUpload: {
        uploadUrl: new URL(imgUrl(tripId, `${u.id}.${u.ext}`)),
        uploadId: u.id,
        localFile: undefined,
        dimensions: u.dimensions,
      },
    })),
  );
  const [uploadingMediaCount, setUploadingMediaCount] = useState<number | null>(null);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const { uploadFiles } = useUploadFiles();
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [confirmingDeleteStory, setConfirmingDeleteStory] = useState(false);
  const [addPhotoTileStyle, setAddPhotoTileStyle] = useState<{ height: string; width: string }>({
    height: '200px',
    width: '200px',
  });

  // Check if form has changes
  const hasFormChanges = useMemo(() => {
    if (!props.story) {
      return false;
    }
    // Check text fields
    const titleChanged = storyTitle !== props.story.title;
    const descriptionChanged = storyDescription !== props.story.description;
    const placeChanged = storyPlace !== props.story.place;

    // Check date field
    const originalDate = props.story.date ? parseISO(props.story.date) : null;
    const currentDate = storyDate?.startDate;
    const dateChanged =
      originalDate?.toISOString().split('T')[0] !== currentDate?.toISOString().split('T')[0];

    // Check media changes (count or order)
    const originalMediaIds = props.story.medias.map((m) => m.id);
    const currentMediaIds = uploadedMedia
      .filter((m) => m.fileUpload)
      .map((m) => m.fileUpload?.uploadId)
      .filter((id): id is string => !!id);
    const mediaChanged = JSON.stringify(originalMediaIds) !== JSON.stringify(currentMediaIds);

    return titleChanged || descriptionChanged || placeChanged || dateChanged || mediaChanged;
  }, [storyTitle, storyDescription, storyPlace, storyDate, uploadedMedia, props.story]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Calculate dimensions for the "Add photo" tile based on the first photo
  useEffect(() => {
    const firstPhoto = uploadedMedia[0]?.fileUpload?.dimensions;
    if (firstPhoto) {
      const maxHeight = 200;
      // Use smaller width percentage on mobile (28vw) vs desktop (40vw)
      const isMobile = window.innerWidth < 768;
      const maxWidthPx = (isMobile ? 28 : 40 / 100) * window.innerWidth;
      const width = Math.round(maxHeight * (firstPhoto.width / firstPhoto.height));
      const finalWidth = Math.min(width, maxWidthPx);
      const finalHeight =
        width > maxWidthPx
          ? Math.round(maxWidthPx * (firstPhoto.height / firstPhoto.width))
          : maxHeight;
      setAddPhotoTileStyle({ height: `${finalHeight}px`, width: `${finalWidth}px` });
    } else {
      // Smaller default size on mobile
      const isMobile = window.innerWidth < 768;
      const defaultSize = isMobile ? '150px' : '200px';
      setAddPhotoTileStyle({ height: defaultSize, width: defaultSize });
    }
  }, [uploadedMedia]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setUploadedMedia((items) => {
        const oldIndex = items.findIndex(
          (item) => item.fileUpload?.uploadUrl.pathname === active.id,
        );
        const newIndex = items.findIndex(
          (item) => item.fileUpload?.uploadUrl.pathname === over?.id,
        );

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleDeleteClick = (mediaId: string) => {
    setConfirmingDelete(mediaId);
  };

  const handleConfirmDelete = (mediaId: string) => {
    setUploadedMedia((prev) =>
      prev.filter((media) => media.fileUpload?.uploadUrl.pathname !== mediaId),
    );
    setConfirmingDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmingDelete(null);
  };

  // Handle clicking outside to cancel confirmation
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (confirmingDelete) {
        const target = event.target as Element;
        // Check if the click is not on the confirmation overlay
        if (!target.closest('.confirmation-overlay')) {
          setConfirmingDelete(null);
        }
      }
    };

    if (confirmingDelete) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [confirmingDelete]);

  const handleDeleteStoryClick = () => {
    setConfirmingDeleteStory(true);
  };

  const handleConfirmDeleteStory = async () => {
    await deleteStory();
  };

  const handleCancelDeleteStory = () => {
    setConfirmingDeleteStory(false);
  };

  const updateStory = async () => {
    if (!props.story) {
      return;
    }
    const uploads = uploadedMedia
      .filter((media) => media.fileUpload)
      .map((media) => {
        const ext = media.fileUpload?.uploadUrl.pathname.split('.').pop() || 'jpg';
        return {
          id: media.fileUpload?.uploadId || '',
          ext: ext,
          dimensions: media.fileUpload?.dimensions || { width: 0, height: 0 },
        };
      })
      .filter((upload) => upload.id); // Filter out any empty IDs
    await patchStory({
      tripId: props.story.tripId,
      storyId: props.story.id,
      partialStoryBodyRequest: {
        title: storyTitle,
        description: storyDescription ?? '',
        place: storyPlace,
        date: storyDate?.startDate?.toLocaleDateString('sv-SE') ?? undefined,
        medias: uploads,
      },
    });
    props.onClose();
  };

  const deleteStory = async () => {
    if (!props.story) {
      return;
    }
    await callDeleteStory({
      tripId: props.story.tripId,
      storyId: props.story.id,
    });
    props.onClose();
  };

  const showFailure = () => {
    setFailedEvent(true);
    setTimeout(() => {
      setFailedEvent(false);
    }, 2000);
  };

  const onFileUploaded = async (fileUploadResult: FileUploadResult) => {
    if (!fileUploadResult.success) {
      console.error(fileUploadResult.error);
      showFailure();
      return;
    }
    setUploadedMedia((prev) => [fileUploadResult, ...prev]);
    setUploadingMediaCount((prev) => (prev || 0) - 1);
  };

  useEffect(() => {
    if (!props.story) {
      return;
    }
    if (filesToUpload && filesToUpload.length > 0) {
      uploadFiles({
        tripId: props.story.tripId,
        storyId: props.story.id,
        files: filesToUpload,
        onUploaded: onFileUploaded,
      }).catch((e) => {
        console.error('error uploading files: ' + JSON.stringify(e));
        showFailure();
      });
      setFilesToUpload([]);
    }
  }, [filesToUpload]);

  return (
    <Dialog open={props.isOpen} onClose={props.onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-4xl rounded-2xl bg-surface shadow-2xl overflow-hidden">
            <div className="bg-primary border-brand px-8 py-6">
              <Dialog.Title className="text-2xl font-bold text-on-dark">Edit Story</Dialog.Title>
            </div>

            {/* Form Content with Better Spacing */}
            <div className="px-8 py-6 space-y-6">
              {/* Alerts Section */}
              {!!uploadingMediaCount && uploadingMediaCount > 0 && (
                <InfoAlert text={`Uploading ${uploadingMediaCount} files...`} />
              )}
              {!!uploadingMediaCount && uploadingMediaCount == 0 && (
                <SuccessAlert text="Upload done" />
              )}
              {failedEvent && <ErrorAlert text="Upload failed" />}

              {/* Story Details Card */}
              <div className="bg-muted rounded-xl p-6 space-y-5 border border-subtle shadow-sm">
                <h3 className="text-lg font-semibold text-main flex items-center gap-2">
                  <span className="text-xl">✍️</span>
                  Story Details
                </h3>

                {/* Date Input with Icon */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-main">
                    <CalendarIcon className="w-4 h-4 text-brand" />
                    When did this happen?
                  </label>
                  <div className="relative">
                    <Datepicker
                      inputId={'story-date'}
                      asSingle={true}
                      value={storyDate}
                      onChange={(newValue) => setStoryDate(newValue)}
                    />
                  </div>
                </div>

                {/* Location Input with Icon */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-main">
                    <MapPinIcon className="w-4 h-4 text-brand" />
                    Where were you?
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPinIcon className="h-5 w-5 text-disabled" />
                    </div>
                    <input
                      id={'story-place'}
                      name={'story-place'}
                      type={'text'}
                      placeholder="e.g., Paris, France"
                      value={storyPlace}
                      onChange={(event) => setStoryPlace(event.target.value)}
                      className="block w-full pl-10 pr-3 py-3 rounded-lg bg-surface border border-interactive
                        text-main placeholder:text-disabled
                        focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent
                        transition-all shadow-sm hover:border-interactive-hover"
                    />
                  </div>
                </div>

                {/* Title Input with Icon */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-main">
                    <SparklesIcon className="w-4 h-4 text-brand" />
                    Give it a title
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SparklesIcon className="h-5 w-5 text-disabled" />
                    </div>
                    <input
                      id={'story-title'}
                      name={'story-title'}
                      type={'text'}
                      placeholder="e.g., Sunny Breakfast ☀️"
                      value={storyTitle}
                      onChange={(event) => setStoryTitle(event.target.value)}
                      className="block w-full pl-10 pr-3 py-3 rounded-lg bg-surface border border-interactive
                        text-main placeholder:text-disabled font-medium
                        focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent
                        transition-all shadow-sm hover:border-interactive-hover"
                    />
                  </div>
                </div>

                {/* Description Textarea */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-main">
                    <span className="text-base">📝</span>
                    Tell your story
                  </label>
                  <textarea
                    id={'story-description'}
                    name={'description'}
                    rows={5}
                    placeholder="We started this sunny morning ❤️ at a lovely Cafè in Florence 🥐☕️"
                    value={storyDescription}
                    onChange={(event) => setStoryDescription(event.target.value)}
                    className="block w-full px-4 py-3 rounded-lg bg-surface border border-interactive
                      text-main placeholder:text-disabled
                      focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent
                      transition-all shadow-sm hover:border-interactive-hover resize-none"
                  />
                </div>
              </div>

              {/* Photo Gallery Card */}
              <div className="bg-muted border-subtle rounded-xl p-6 space-y-4 border shadow-sm">
                <h3 className="text-lg font-semibold text-main flex items-center gap-2">
                  <PhotoIcon className="w-5 h-5 text-brand" />
                  Photos
                  {uploadedMedia.length > 0 && (
                    <span className="text-sm font-normal text-muted">
                      ({uploadedMedia.length} {uploadedMedia.length === 1 ? 'photo' : 'photos'})
                    </span>
                  )}
                </h3>

                <div className="flex flex-wrap gap-3 justify-center">
                  {/* Add Photo Button */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="group rounded-2xl border-2 border-dashed border-brand bg-white/50
                      hover:bg-surface hover:border-brand hover:shadow-lg
                      flex items-center justify-center cursor-pointer transition-all duration-300
                      transform hover:scale-105"
                    style={{
                      height: addPhotoTileStyle.height,
                      width: addPhotoTileStyle.width,
                    }}
                  >
                    <div className="text-center text-brand group-hover:text-brand">
                      <PhotoIcon className="w-10 h-10 mx-auto mb-2 opacity-70 group-hover:opacity-100 transition-opacity" />
                      <div className="text-sm font-semibold">Add Photos</div>
                      <div className="text-xs text-muted mt-1">Click to upload</div>
                    </div>
                  </div>

                  {/* Photo Grid with Drag & Drop */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={uploadedMedia
                        .filter((media) => media.fileUpload)
                        .map((media) => media.fileUpload?.uploadUrl.pathname || '')}
                      strategy={rectSortingStrategy}
                    >
                      {uploadedMedia
                        .filter((media) => media.fileUpload)
                        .map((media) => {
                          const pathname = media.fileUpload?.uploadUrl.pathname || '';
                          return (
                            <div key={pathname} className="relative group">
                              <DynamicImage
                                id={pathname}
                                className="max-h-[200px] w-auto max-w-[28vw] md:max-w-[40vw] rounded-2xl object-cover
                              shadow-md hover:shadow-xl transition-shadow border-2 border-surface"
                                src={imgUrl(tripId, thumbnail(path.basename(pathname)))}
                                defaultSrc={'/spinner.gif'}
                                sortable={true}
                              />
                              {confirmingDelete === pathname ? (
                                <div
                                  className="confirmation-overlay absolute inset-0 bg-black/70 backdrop-blur-sm
                                flex items-center justify-center rounded-2xl"
                                >
                                  <div className="flex flex-col gap-2 p-4">
                                    <p className="text-on-dark font-medium text-center mb-2">
                                      Delete this photo?
                                    </p>
                                    <div className="flex gap-2">
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => handleConfirmDelete(pathname)}
                                      >
                                        Delete
                                      </Button>
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleCancelDelete}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteClick(pathname)}
                                  className="absolute top-2 right-2 p-2 rounded-full opacity-0 group-hover:opacity-100 hover:scale-110"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                    </SortableContext>
                  </DndContext>
                </div>
              </div>
            </div>

            {/* Modern Action Bar */}
            <div className="bg-muted border-subtle px-8 py-5">
              {confirmingDeleteStory ? (
                <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
                  <div className="flex items-center gap-2 text-error font-medium mb-2 sm:mb-0">
                    <TrashIcon className="w-5 h-5" />
                    <span>Are you sure?</span>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="destructive"
                      disabled={patchStoryLoading || deleteStoryLoading}
                      loading={deleteStoryLoading}
                      onClick={handleConfirmDeleteStory}
                    >
                      {deleteStoryLoading ? 'Deleting...' : 'Yes, Delete Story'}
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={patchStoryLoading || deleteStoryLoading}
                      onClick={handleCancelDeleteStory}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
                  <Button
                    variant="primary"
                    size="lg"
                    className="flex-1"
                    disabled={
                      patchStoryLoading ||
                      deleteStoryLoading ||
                      !hasFormChanges ||
                      (uploadingMediaCount ?? 0) > 0
                    }
                    loading={patchStoryLoading}
                    onClick={() => updateStory()}
                  >
                    {patchStoryLoading ? 'Saving...' : 'Save Changes'}
                  </Button>

                  <Button
                    variant="secondary"
                    disabled={patchStoryLoading || deleteStoryLoading}
                    onClick={props.onClose}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="destructive"
                    disabled={patchStoryLoading || deleteStoryLoading}
                    onClick={handleDeleteStoryClick}
                    title="Delete Story"
                    iconBefore={<TrashIcon className="w-5 h-5" />}
                    className="group"
                  >
                    <span>Delete Story</span>
                  </Button>
                </div>
              )}
            </div>

            <input
              multiple={true}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(event) => {
                const files = event.target.files;
                if (files) {
                  setUploadingMediaCount((prev) => (prev || 0) + files.length);
                  setFilesToUpload(Array.from(files));
                }
              }}
            />
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};
